<script lang="ts">
    import { _ } from "svelte-i18n"
    import { getTopicById, topics } from "../../../../store"
    import MedialibImage from "../../../widgets/MedialibImage.svelte"
    import MediaQuery from "../../../widgets/MediaQuery.svelte"
    import MoreButton from "../../../widgets/MoreButton.svelte"
    import SocialShare from "../../../widgets/SocialShare.svelte"
    import CookieSet from "../../../widgets/CookieSet.svelte"
    import VideoJs from "../VideoTextRow/VideoJs.svelte"
    import { onMount } from "svelte"
    import { animateOnVisible } from "../../../../configs/config"
    import { spaNavigate } from "../../../../functionTools/routing"
    import { parseSecondaryLink } from "./link-utils"

    let {
        entry,
        isPrimary,
        largeDescription = false,
        disableTransitions = false,
    }: {
        entry: ArticleEntryContent
        isPrimary?: boolean
        largeDescription?: boolean
        disableTransitions?: boolean
    } = $props()
    let topicsSet = $derived(Object.values($topics).length > 0)
    const toBoolean = (value: unknown): boolean => value === true || value === "true" || value === 1 || value === "1"
    const extractVideoId = (link: string) => {
        const regex = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{11})/
        const match = link?.match(regex)
        return match ? match[1] : null
    }
    const isVideoPreview = $derived(toBoolean(entry.use_video_preview) && !!entry.video_url)
    const videoId = $derived(isVideoPreview ? extractVideoId(entry.video_url) : null)
    const embedUrl = $derived(
        isVideoPreview && videoId
            ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&showinfo=0`
            : entry.video_url
    )
    let play = $state(false)

    let el = $state(null)
    onMount(() => {
        if (!el) return

        animateOnVisible(el, {
            opacity: [0.5, 1],
            scale: [0.5, 1],
            translateY: isPrimary ? ["0px", "0rem"] : ["40px", "0rem"],
            duration: 300,
            easing: "easeInOutQuad",
        })
    })

    function getPrimaryLink() {
        if (!entry.disable_primary) return `/${entry.id}`
        if (entry.secondary_links && entry.secondary_links.length > 0) {
            return parseSecondaryLink(entry.secondary_links[0].link)
        }
    }

    function openLinkInNewTab(link: string) {
        if (link.startsWith("http")) {
            return true
        } else {
            return false
        }
    }

    function handleCardClick() {
        if (isVideoPreview) return
        const primaryLink = getPrimaryLink()
        if (primaryLink) {
            const newTab = openLinkInNewTab(primaryLink)
            if (newTab) {
                window.open(primaryLink, "_blank")
            } else spaNavigate(primaryLink)
        }
    }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
{#snippet articleBody(entry: ArticleEntryContent)}
    <main>
        <h5 class="subtitle">
            {entry.subtitle}
        </h5>
        <p class="teaser" style={isPrimary || largeDescription ? "font-size: 1rem;" : "font-size: 0.7rem;"}>
            {entry.description}
        </p>
        {#if entry.person_reference}
            <div class="small-dividor"></div>
            <small style="font-size: 0.7rem; margin-top: -0.3rem;">
                {entry.person_reference}
            </small>
        {/if}
    </main>
    <footer class="footer" style="margin-top: 0.6rem;">
        <ul>
            {#if entry.secondary_links}
                {#each entry.secondary_links as link (link.link || link.text)}
                    <li>
                        <MoreButton
                            size={isPrimary || largeDescription ? "big" : "small"}
                            text={link.text}
                            path={parseSecondaryLink(link.link)}
                            type="dark"
                            target={!link.target ? (openLinkInNewTab(link.link) ? "_blank" : "_self") : link.target}
                        />
                    </li>
                {/each}
            {/if}
            {#if !entry.disable_primary}
                <li>
                    <MoreButton
                        text={$_("general.more-know")}
                        path="/{entry.id}"
                        type="dark"
                        size={isPrimary || largeDescription ? "big" : "small"}
                    />
                </li>
            {/if}
        </ul>
        <div class="share-row">
            <small>{$_("general.share")}</small>
            <div class="share-icons">
                <SocialShare
                    path="/{entry.id}"
                    teaser={entry.subtitle}
                    title={entry.title}
                    image={entry.meta_img}
                    hashtags={["#StiftungFamilienunternehmen"]}
                />
            </div>
        </div>
    </footer>
{/snippet}

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
    class="article-preview"
    bind:this={el}
    class:primary={isPrimary}
    class:video-card={isVideoPreview}
    onclick={!isVideoPreview ? handleCardClick : undefined}
>
    <div class="upper" class:disableTransitions>
        <div class="image" class:video={isVideoPreview}>
            {#if isVideoPreview}
                <div class="video-shell" class:playing={play}>
                    {#if !play}
                        <button
                            type="button"
                            class="video-trigger"
                            onclick={(event) => {
                                event.stopPropagation()
                                play = true
                            }}
                        >
                            <MedialibImage
                                id={entry.hover_preview_image && typeof entry.hover_preview_image == "string"
                                    ? entry.hover_preview_image
                                    : entry.background_image}
                                alt={entry.title}
                                maxWidth={isPrimary ? 700 : 200}
                            />
                            <div class="video-overlay">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="48"
                                    height="48"
                                    viewBox="0 0 72 72"
                                    fill="none"
                                >
                                    <path
                                        d="M61.227 28.0589C62.6679 28.8252 63.8731 29.969 64.7136 31.3679C65.5541 32.7668 65.9981 34.368 65.9981 35.9999C65.9981 37.6319 65.5541 39.2331 64.7136 40.632C63.8731 42.0309 62.6679 43.1747 61.227 43.9409L22.791 64.8419C16.602 68.2109 9 63.8309 9 56.9039V15.0989C9 8.16895 16.602 3.79195 22.791 7.15495L61.227 28.0589Z"
                                        stroke="white"
                                        stroke-width="1.5"
                                    ></path>
                                </svg>
                                <span>{$_("general.play")}</span>
                            </div>
                        </button>
                    {/if}
                    {#if play}
                        <CookieSet
                            cookieName="youtube"
                            textPosition="unten"
                            background="rgba(44, 44, 44, 0.4)"
                            minHeight={0}
                            force={!videoId}
                        >
                            {#snippet contentSnippet()}
                                <VideoJs
                                    src={embedUrl}
                                    muted={false}
                                    controlPlayFromOutside={true}
                                    {play}
                                    enableAutoplayOnVisible={false}
                                />
                            {/snippet}
                        </CookieSet>
                    {/if}
                </div>
            {:else}
                <MedialibImage id={entry.background_image} alt={entry.title} maxWidth={isPrimary ? 700 : 200} />
                <MedialibImage
                    id={entry.hover_preview_image && typeof entry.hover_preview_image == "string"
                        ? entry.hover_preview_image
                        : entry.background_image}
                    alt={entry.title}
                />
            {/if}
        </div>
        <div class="content">
            <header class="header">
                <div class="title">
                    {#if entry.tag}
                        <div class="tag small">{entry.tag}</div>
                    {/if}
                    {#if getTopicById(entry.topics_reference)?.length && topicsSet}
                        <div class="topic">
                            <strong>{$_("general.topic")}</strong>

                            {#each getTopicById(entry.topics_reference) as topic, i (topic?.uuid || topic?.slug || i)}
                                {topic.title}{#if i < getTopicById(entry.topics_reference).length - 1},&nbsp;{/if}
                            {/each}
                        </div>
                    {/if}
                </div>
                {#if entry.publication_date}
                    <time class="date" datetime={entry.publication_date}
                        >{new Date(entry.publication_date).toLocaleDateString("de-DE")}</time
                    >{/if}
            </header>
            <h3 class="title">{entry.title}</h3>
            <MediaQuery query="(min-width: 768px)">
                {#snippet contentSnippet()}
                    {@render articleBody(entry)}
                {/snippet}
            </MediaQuery>
        </div>
    </div>
    <MediaQuery query="(max-width: 767px)">
        {#snippet contentSnippet()}
            {@render articleBody(entry)}
        {/snippet}
    </MediaQuery>
</div>

<style lang="less" global>
    @import "../../../../css/variables.less";
    .article-preview {
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
        border-bottom: 1px solid var(--dark-20);
        padding: 1.2rem 0px;
        width: 100%;
        cursor: pointer;
        height: fit-content;
        &.video-card {
            cursor: default;
        }

        .upper {
            display: flex;
            gap: 1.2rem;
            width: 100%;
            &:hover {
                /* svelte-ignore css_unused_selector */
                .image:not(.video) img:last-of-type {
                    opacity: 1;
                }

                transform: translateY(-20px);
                &.disableTransitions {
                    transform: translateY(0px);
                }
            }
            &.disableTransitions {
                transform: translateY(0px);
            }

            .image {
                background: radial-gradient(circle, #24222000, #24222033);
                width: 176px;
                aspect-ratio: 1/1;
                height: 176px;
                position: relative;
                border-radius: 4px;
                picture {
                    display: block;
                    width: 100%;
                    height: 100%;
                }
                /* svelte-ignore css_unused_selector */
                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: 4px;
                }
                &:not(.video) {
                    picture:first-of-type img {
                        position: absolute;
                        inset: 0;
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                        border-radius: 4px;
                        z-index: 1;
                    }
                    /* svelte-ignore css_unused_selector */
                    picture:last-of-type {
                        /* svelte-ignore css_unused_selector */
                        img {
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            opacity: 0;
                            transition: opacity 0.3s ease-in-out;
                            z-index: 2;
                        }
                    }
                }
                &.video {
                    width: 100%;
                    height: 292px;
                    .video-shell {
                        position: relative;
                        width: 100%;
                        height: 100%;
                        aspect-ratio: inherit;
                        border-radius: 4px;
                        overflow: hidden;
                        background: var(--bg-100);
                        .video-trigger {
                            all: unset;
                            cursor: pointer;
                            width: 100%;
                            height: 100%;
                            display: block;
                            position: relative;
                            /* svelte-ignore css_unused_selector */
                            img {
                                width: 100%;
                                height: 100%;
                                object-fit: cover;
                                border-radius: 4px;
                            }
                            .video-overlay {
                                position: absolute;
                                inset: 0;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                gap: 0.6rem;
                                background: linear-gradient(180deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.65) 100%);
                                color: var(--bright-100);
                                font-weight: 600;
                                letter-spacing: 0.02em;
                                text-transform: uppercase;
                                svg {
                                    filter: drop-shadow(0 0 12px rgba(0, 0, 0, 0.4));
                                }
                            }
                        }
                        &.playing {
                            .video-trigger {
                                display: none;
                            }
                        }
                        .video-container {
                            width: 100%;
                            height: 100%;
                            padding-bottom: 0;
                            aspect-ratio: inherit;
                        }
                        .video-container iframe,
                        video {
                            width: 100%;
                            height: 100%;
                            object-fit: cover;
                        }
                    }
                }
                @media @max-mobile {
                    width: 120px;
                    height: 120px;
                    &.video {
                        width: 100%;
                        max-width: 100%;
                        min-height: 292px;
                        height: 292px;
                        flex-basis: 292px;
                    }
                }
            }

            .content {
                display: flex;
                flex-direction: column;
                height: fit-content;
                gap: 0.3rem;
                h3.title {
                    overflow-wrap: anywhere;
                    hyphens: auto;
                }
                & > header.header {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    .title {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 0.3rem;
                        align-items: center;

                        .tag {
                            flex-shrink: 0;
                        }

                        .topic {
                            text-transform: uppercase;
                            hyphens: none;
                            overflow-wrap: normal;
                            word-break: normal;

                            strong {
                                padding-right: 0.3rem;
                                text-transform: uppercase;
                                color: var(--dark-100);
                                font-size: 0.5rem;
                            }
                            font-size: 0.5rem;
                        }
                    }
                    time {
                        font-size: 0.5rem;
                        white-space: nowrap;
                    }
                }
            }
        }
        &.video-card {
            width: 100%;
            .upper {
                flex-direction: row;
                align-items: flex-start;
                gap: 0.8rem;
                width: 100%;
                &:hover {
                    transform: none;
                }
            }
        }

        main {
            display: flex;
            flex-direction: column;
            gap: 0.6rem;
            h5 {
                font-size: 0.7rem;
                font-style: normal;
                text-transform: uppercase;
                font-weight: 400;
            }
            p {
                font-size: 0.7rem;
            }
        }
        footer {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            & > ul {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                li {
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                }
            }
            .share-row {
                display: flex;
                gap: 0.6rem;
                align-items: center;
                text-align: center;
                small {
                    display: flex;
                    text-align: center;
                    align-items: center;

                    font-size: 0.5rem;
                    text-transform: uppercase;
                }
                .share-icons {
                    display: flex;
                    align-items: center;
                    gap: 0.3rem;
                }
            }
        }
        &.primary {
            display: flex;
            flex-direction: column;
            gap: 0.6rem;
            background-color: var(--bg-100);

            .image {
                width: 100%;
                height: 400px;
                @media @max-mobile {
                    height: 240px;
                }
            }
            .image.video {
                width: 100%;
                max-width: 100%;
                height: auto;
                aspect-ratio: 16/9;
                flex: 1 1 100%;
                min-height: unset;
            }
        }
    }

    @media @max-mobile {
        .article-preview.video-card {
            .upper {
                flex-direction: row;
                align-items: flex-start;
                gap: 0.8rem;
                .image {
                    &.video {
                        max-width: 100%;
                        width: 100%;
                    }
                }
            }
        }
    }
</style>
